import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { deleteSyncPair } from '~/api/evisync';
import { getProcurementIntegrations } from '~/common/constants/integrations';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Button } from '~/eds/src/components/Button/Button';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Filename } from '~/eds/src/components/Filename/Filename';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Section } from '~/eds/src/components/Section/Section';
import { Text } from '~/eds/src/components/Text/Text';
import { Table } from '~/eds/src/modules/Table/Table';
import { AribaIntegrationType } from '~/enums/AribaIntegrationType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { FileMimeType } from '~/enums/FileMimeType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { SyncPairStatusType } from '~/enums/SyncPairStatusType';
import { AribaConfigPanel } from '~/features/integrations/procurement/AribaConfigPanel';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePollingRequest } from '~/hooks/usePollingRequest';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { fileTypesToChips } from '~/utils/fileTypesToChips';

import { resetSyncPairAuthData } from '../util';
import EmptySyncPairsPlaceholder from './EmptySyncPairsPlaceholder';

function ProcurementIntegrations() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const PAGE_SIZE = 50;

  const enableSyncConfiguration = useFlag(FlagType.SyncConfiguration);
  const { toast } = useToast();

  const { params } = useRouting();
  const { clientId } = params;

  const [syncPairToDelete, setSyncPairToDelete] = useState(null);
  const [syncPairToConfig, setSyncPairToConfig] = useState(null);
  const [fileConfig, setFileConfig] = useState(null);
  const [deletingIds, setDeletingIds] = useState([]);
  const [page, setPage] = useState(1);
  const [activeConfig, setActiveConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [isMissingColumn, setIsMissingColumn] = useState(false);
  const [validateCsv, validateResult] =
    api.endpoints.validateAribaDocumentIdFile.useMutation();
  const { isLoading: isLoadingValidate } = validateResult;
  const [updateSyncPair, updateFileResult] =
    api.endpoints.updateAribaFileSyncPair.useMutation();
  const { isLoading: isLoadingUpdate } = updateFileResult;

  const {
    data: aribaSyncPairs = [],
    isLoading: isLoadingSyncPairs,
    error: aribaSyncPairsError,
  } = usePollingRequest(
    api.endpoints.getSyncPairsAriba.useQuery,
    { clientId },
    { pollingInterval: 20000 },
  );

  useEffect(() => {
    if (aribaSyncPairsError) {
      toast({
        message: intl.formatMessage({
          id: 'ProcurementIntegrations.fetchSyncPairsError',
          defaultMessage: 'Unable to fetch sync pairs, please try again.',
          description: 'Error message when failing to fetch sync pairs',
        }),
        status: 'danger',
      });

      dispatch(slices.integrations.actions.setSyncPairs([]));
    }

    if (aribaSyncPairs) {
      dispatch(slices.integrations.actions.setSyncPairs(aribaSyncPairs));
    }
  }, [aribaSyncPairsError, aribaSyncPairs]);

  const transformFileTypes = (fileTypes) => {
    return fileTypes ? fileTypes.map((f) => f.split(',')).flat() : '';
  };

  const handlePaginate = ({ pageIndex }) => {
    setPage(pageIndex);
  };

  const getIsOneTimeSync = (props) => {
    if (props.providerOptions?.syncType) {
      return props.providerOptions.syncType === AribaIntegrationType.ONE_TIME;
    } else {
      return props.providerOptions.tokens?.length === 1;
    }
  };

  const columns = [
    {
      key: 'provider',
      title: intl.formatMessage({
        id: 'ProcurementIntegrations.typeColumn',
        defaultMessage: 'Type',
        description: 'Column header for provider type',
      }),
      renderCell: ({ provider }) => (
        <Layout spacing={2} align="center">
          <ProviderLogo provider={provider} size="s" />

          <Text>{getProcurementIntegrations()[provider].name}</Text>
        </Layout>
      ),
    },
    {
      key: 'status',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'ProcurementIntegrations.statusColumn',
        defaultMessage: 'Status',
        description: 'Column header for sync pair status',
      }),
      mapCellProps: (props) => {
        let icon;
        let text;
        let status;
        const isOneTimeSync = getIsOneTimeSync(props);
        switch (props.status) {
          case SyncPairStatusType.AuthError:
            text = intl.formatMessage({
              id: 'ProcurementIntegrations.authenticationError',
              defaultMessage: 'Authentication error',
              description: 'Status message for authentication errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.OtherError:
          case SyncPairStatusType.DataAccessError:
          case SyncPairStatusType.OutOfSpaceError:
            text = intl.formatMessage({
              id: 'ProcurementIntegrations.providerError',
              defaultMessage: 'Provider error',
              description: 'Status message for provider errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.ConfigError:
            text = intl.formatMessage({
              id: 'ProcurementIntegrations.configurationError',
              defaultMessage: 'Configuration error',
              description: 'Status message for configuration errors',
            });
            status = 'danger';
            break;
          case SyncPairStatusType.Active:
            if (isOneTimeSync) {
              text = intl.formatMessage({
                id: 'ProcurementIntegrations.oneTimeImport',
                defaultMessage: 'One-time import',
                description: 'Status message for one-time import sync pairs',
              });
              status = 'warning';
            } else {
              text = intl.formatMessage({
                id: 'ProcurementIntegrations.active',
                defaultMessage: 'Active',
                description: 'Status message for active sync pairs',
              });
              status = 'new';
            }
            break;
          case SyncPairStatusType.Initial:
            if (isOneTimeSync) {
              text = intl.formatMessage({
                id: 'ProcurementIntegrations.importing',
                defaultMessage: 'Importing',
                description: 'Status message for importing sync pairs',
              });
            } else {
              text = intl.formatMessage({
                id: 'ProcurementIntegrations.syncing',
                defaultMessage: 'Syncing',
                description: 'Status message for syncing sync pairs',
              });
            }
            icon = 'loading';
            status = 'info';
            break;
          case SyncPairStatusType.Syncing:
            icon = 'loading';
            text = intl.formatMessage({
              id: 'ProcurementIntegrations.syncingStatus',
              defaultMessage: 'Syncing',
              description: 'Status message for sync pairs that are syncing',
            });
            status = 'info';
            break;
          case SyncPairStatusType.ActiveSync:
            text = intl.formatMessage({
              id: 'ProcurementIntegrations.activeSync',
              defaultMessage: 'Active',
              description: 'Status message for active sync status',
            });
            status = 'new';
            break;
          default:
            text = intl.formatMessage({
              id: 'ProcurementIntegrations.unknownError',
              defaultMessage: 'Unknown Error',
              description: 'Status message for unknown errors',
            });
            status = 'danger';
        }
        const statusChip = {
          icon,
          text,
          status,
        };
        return {
          chips: [statusChip],
        };
      },
    },
    {
      key: 'externalSource',
      title: intl.formatMessage({
        id: 'ProcurementIntegrations.externalSource',
        defaultMessage: 'External Source',
        description: 'Column header for external source',
      }),
      info: intl.formatMessage({
        id: 'ProcurementIntegrations.realmIdInfo',
        defaultMessage: 'Realm ID for Ariba',
        description: 'Info tooltip for Realm ID column',
      }),
      cellType: 'text',
      mapCellProps: ({ providerOptions }) => ({
        text: providerOptions.realm,
      }),
    },
    {
      key: 'evisortFolderPath',
      cellType: 'filesystem',
      title: intl.formatMessage({
        id: 'ProcurementIntegrations.evisortFolder',
        defaultMessage: 'Evisort Folder',
        description: 'Column header for Evisort folder path',
      }),
      mapCellProps: ({ id, evisortFolderPath }) => ({
        folder: {
          id,
          name: intl.formatMessage({
            id: 'ProcurementIntegrations.documentsFolder',
            defaultMessage: 'Documents',
            description: 'Default folder name for documents',
          }),
          path: evisortFolderPath,
          type: 'internal',
        },
      }),
    },
    {
      key: 'allowedSyncFileTypes',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'ProcurementIntegrations.fileTypes',
        defaultMessage: 'File Types',
        description: 'Column header for allowed file types',
      }),
      mapCellProps: ({ allowedSyncFileTypes }) => ({
        chips: allowedSyncFileTypes
          ? fileTypesToChips(transformFileTypes(allowedSyncFileTypes))
          : [],
      }),
      mapCellValue: ({ allowedSyncFileTypes }) =>
        allowedSyncFileTypes ? allowedSyncFileTypes.join(',') : [],
      width: 'l',
    },
    {
      key: 'dateUpdated',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'ProcurementIntegrations.lastSyncActivity',
        defaultMessage: 'Last Sync Activity',
        description: 'Column header for last sync activity date',
      }),
      mapCellProps: ({ dateUpdated }) => ({
        datetime: dateUpdated,
      }),
      minWidth: 'auto',
    },
  ];

  const onClickDelete = async () => {
    try {
      setDeletingIds([...deletingIds, syncPairToDelete.id]);
      await deleteSyncPair(syncPairToDelete.id, clientId);
      dispatch(slices.integrations.actions.removeSyncPair(syncPairToDelete));
      toast({
        status: 'success',
        message: intl.formatMessage(
          {
            id: 'ProcurementIntegrations.deleteSyncPairSuccess',
            defaultMessage: '{name} sync pair has been deleted.',
            description:
              'Success message when sync pair is deleted. Example: name = "Document Name"',
          },
          { name: syncPairToDelete.name },
        ),
      });
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'ProcurementIntegrations.deleteSyncPairError',
          defaultMessage: 'Unable to delete sync pair, please try again.',
          description: 'Error message when failing to delete sync pair',
        }),
      });
    }
  };

  const handleHideConfigurationDetailsPanel = () => {
    setActiveConfig(null);
  };

  const handleHideDeleteModal = () => {
    setSyncPairToDelete(null);
  };

  const handleHideUploadModal = () => {
    setIsMissingColumn(false);
    setFileConfig(null);
    setSyncPairToConfig(null);
  };

  const getFileUploadModalTitle = (syncPair) => {
    if (syncPair) {
      const { evisortFolderPath } = syncPair;
      return `${evisortFolderPath} - Upload new CSV`;
    }
  };

  const onClickUploadFile = () => {
    if (fileConfig) {
      updateSyncPair({
        syncPairId: syncPairToConfig.id,
        file: fileConfig,
      })
        .unwrap()
        .then(() => {
          const { evisortFolderPath } = syncPairToConfig;
          toast({
            status: 'success',
            message: intl.formatMessage(
              {
                id: 'ProcurementIntegrations.uploadCsvSuccess',
                defaultMessage: '{folderPath} - new CSV uploaded.',
                description:
                  'Success message when CSV is uploaded. Example: folderPath = "Contracts"',
              },
              { folderPath: evisortFolderPath },
            ),
          });
          setSyncPairToConfig(null);
          setFileConfig(null);
        })
        .catch((err) => {
          const message = err.response.data?.error;
          toast({
            status: 'danger',
            message: intl.formatMessage(
              {
                id: 'ProcurementIntegrations.uploadFileError',
                defaultMessage: 'Error uploading file: {message}',
                description:
                  'Error message when file upload fails. Example: message = "Processing complete"',
              },
              { message },
            ),
          });
        });
    }
  };

  const getIsUploadFile = (syncPair) => {
    if (syncPair) {
      const { providerOptions } = syncPair;
      return (
        syncPair.status === SyncPairStatusType.Active &&
        providerOptions.syncType === AribaIntegrationType.ONE_TIME
      );
    } else {
      return false;
    }
  };

  const handleSelectedFile = (docIdsFile) => {
    if (!docIdsFile) {
      return;
    }
    const file = docIdsFile[0];
    validateCsv({
      file,
      syncPairToConfig,
    })
      .unwrap()
      .then(() => {
        setIsMissingColumn(false);
        setFileConfig(file);
      })
      .catch((err) => {
        const message = err.response.data?.error;
        if (message.includes('column is missing')) {
          // just show the warning message
          setIsMissingColumn(true);
          setFileConfig(file);
        } else {
          toast({
            status: 'danger',
            message: intl.formatMessage(
              {
                id: 'ProcurementIntegrations.processFileError',
                defaultMessage: 'Error processing file: {message}',
                description:
                  'Error message when file processing fails. Example: message = "Processing complete"',
              },
              { message },
            ),
          });
        }
      });
  };

  if (!isLoadingSyncPairs && aribaSyncPairs.length === 0) {
    return <EmptySyncPairsPlaceholder />;
  }

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'ProcurementIntegrations.reauthenticate',
        defaultMessage: 'Reauthenticate',
        description: 'Row action to reauthenticate sync pair',
      }),
      onClick: (d) => {
        dispatch(
          slices.integrations.actions.setActiveSyncPair({
            ...resetSyncPairAuthData(d),
            isTokenReauthenticate: true,
          }),
        );
        dispatch(
          slices.integrations.actions.setManageSyncPairStage(
            ManageSyncPairStageType.MultiAPIs,
          ),
        );
      },
      condition: (d) =>
        [
          SyncPairStatusType.Active,
          SyncPairStatusType.AuthError,
          SyncPairStatusType.ConfigError,
          SyncPairStatusType.OtherError,
        ].includes(d.status),
    },
    {
      label: intl.formatMessage({
        id: 'ProcurementIntegrations.uploadCsv',
        defaultMessage: 'Upload CSV',
        description: 'Row action to upload CSV file',
      }),
      onClick: (d) => {
        setSyncPairToConfig(d);
      },
      condition: (d) => getIsUploadFile(d),
    },
    {
      label: intl.formatMessage({
        id: 'ProcurementIntegrations.delete',
        defaultMessage: 'Delete',
        description: 'Row action to delete sync pair',
      }),
      onClick: (d) => {
        setSyncPairToDelete(d);
      },
    },
  ];

  if (enableSyncConfiguration) {
    rowActions.push(
      {
        label: intl.formatMessage({
          id: 'ProcurementIntegrations.configuration',
          defaultMessage: 'Configuration',
          description: 'Row action to view configuration',
        }),
        onClick: (d) => {
          setActiveConfig(d);
          setActiveTab('config');
        },
        condition: (d) => d,
      },
      {
        label: intl.formatMessage({
          id: 'ProcurementIntegrations.errorLog',
          defaultMessage: 'Error Log',
          description: 'Row action to view error log',
        }),
        onClick: (d) => {
          setActiveConfig(d, 'errors');
          setActiveTab('errors');
        },
        condition: (d) => d,
      },
    );
  }

  const procurementConfigPanel = (
    <AribaConfigPanel
      config={activeConfig}
      tab={activeTab}
      onHide={handleHideConfigurationDetailsPanel}
    />
  );

  const deleteConfirmationText = intl.formatMessage({
    id: 'ProcurementIntegrations.deleteConfirmationText',
    defaultMessage:
      'Are you sure you want to delete this sync pair?<br/>Deleting the sync pair will not stop an active file transfer, and will not delete any files that have been transferred. <strong>This action cannot be reverted.</strong>',
    description: 'Confirmation text for deleting sync pair',
  });

  const warningMessage = intl.formatMessage({
    id: 'ProcurementIntegrations.warningMessage',
    defaultMessage:
      'We did not find a Primary Document designation, which is necessary to send Evisort data back to Ariba. Continuing without a Primary Document designation will not impact your ability to sync files and data into Evisort.',
    description: 'Warning message for missing primary document designation',
  });

  return (
    <>
      <Layout justify="flex-end" pb={4}>
        <Button
          text={intl.formatMessage({
            id: 'ProcurementIntegrations.addSyncPair',
            defaultMessage: 'Add Sync Pair',
            description: 'Button text to add new sync pair',
          })}
          onClick={() =>
            dispatch(
              slices.integrations.actions.setManageSyncPairStage(
                ManageSyncPairStageType.ProviderProcurements,
              ),
            )
          }
          variant="primary"
        />
      </Layout>
      <Table
        name="storage-integrations"
        isLoading={isLoadingSyncPairs}
        columns={columns}
        totalCount={
          aribaSyncPairs.filter(
            (syncPair) => !deletingIds.includes(syncPair.id),
          ).length
        }
        data={aribaSyncPairs
          .filter((syncPair) => !deletingIds.includes(syncPair.id))
          .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
        rowActions={rowActions}
        options={{ enableManageColumns: false }}
        state={{ pageIndex: page, pageSize: PAGE_SIZE }}
        onPaginate={handlePaginate}
      />
      <Modal
        enableHideOnPrimaryAction={true}
        isVisible={syncPairToDelete}
        onHide={handleHideDeleteModal}
        primaryAction={{
          variant: 'danger',
          text: intl.formatMessage({
            id: 'ProcurementIntegrations.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text in confirmation modal',
          }),
          onClick: onClickDelete,
        }}
        title={intl.formatMessage({
          id: 'ProcurementIntegrations.deleteSyncPairTitle',
          defaultMessage: 'Delete Sync Pair?',
          description: 'Title for delete sync pair modal',
        })}
      >
        <Markdown text={deleteConfirmationText} />
      </Modal>
      <Modal
        isVisible={syncPairToConfig}
        onHide={handleHideUploadModal}
        primaryAction={{
          text: intl.formatMessage({
            id: 'ProcurementIntegrations.saveChanges',
            defaultMessage: 'Save Changes',
            description: 'Button text to save changes',
          }),
          onClick: onClickUploadFile,
          isLoading: isLoadingValidate || isLoadingUpdate,
        }}
        title={getFileUploadModalTitle(syncPairToConfig)}
      >
        {isMissingColumn && (
          <Layout py={2}>
            <Section
              statusMessage={{ message: warningMessage, status: 'danger' }}
            />
          </Layout>
        )}
        <FileInput
          accept={{ [FileMimeType.Csv]: [FileExtensionType.Csv] }}
          onChange={handleSelectedFile}
          name={intl.formatMessage({
            id: 'ProcurementIntegrations.uploadDocument',
            defaultMessage: 'Upload Document',
            description: 'Label for file upload input',
          })}
          isLoading={isLoadingValidate}
          isMultiple={false}
        />
        {fileConfig && (
          <Layout spacing={4} py={4} direction="column">
            <Filename name={fileConfig.name} includeExtension={true} />
          </Layout>
        )}
      </Modal>
      {activeConfig && procurementConfigPanel}
    </>
  );
}

export default ProcurementIntegrations;
